import { useEffect } from 'react';
import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import BaseModalStore from '@src/components/modal-container/base-modal-store';
import Toast from '@src/components/toast';
import { noop } from '@src/utils/common-util';
import services from './services';
import $$tracker from '@src/tracker';

class FriendRecallModalStore extends BaseModalStore {
  @observable mode = 'list';

  @observable loading = false;

  @observable loadSucceeded = false;

  @observable.shallow friends = [];

  @observable.ref pendingFriend = null;

  @observable submitting = false;

  submitPromise = null;

  loadPromise = null;

  // 召回门控（2026-09-24 需求）：打开弹窗前先取列表，无可召回好友时不展示弹窗直接走海报入口
  recallGatePromise = null;

  gatePrefetched = false;

  loadGeneration = 0;

  mountGeneration = 0;

  activeMountToken = null;

  loadMountToken = null;

  reportedMountToken = null;

  mountOnClose = noop;

  onOwnerClose = noop;

  onCreatePoster = noop;

  services = services;

  toast = Toast;

  constructor(options = {}) {
    const { onClose = noop, ...modalOptions } = options;
    super(modalOptions);
    Object.assign(this, modalOptions);
    this.onOwnerClose = options.onOwnerClose || onClose;
  }

  use(mountOnClose = noop) {
    this.mountOnClose = mountOnClose;
    super.use();
    useEffect(() => {
      const mountToken = this.mountGeneration + 1;
      this.mountGeneration = mountToken;
      this.activeMountToken = mountToken;
      if (!this.consumeGatePrefetch(mountToken)) {
        this.loadPromise = this.loadFriends(mountToken);
      }
      return () => {
        this.invalidateMount(mountToken);
      };
    }, []);
  }

  /**
   * 弹窗打开前的召回门控：先加载召回列表，无可召回好友（含后端 -20142 归一化
   * 后的空列表）时不展示弹窗、直接委托海报入口；有好友才打开弹窗并复用预取数据。
   * 列表拉取耗时较长（2026-09-24 需求）：等待期间用带遮罩的全局 loading 占位，
   * 遮罩同时阻止等待期内的重复点击（与 Promise 锁双保险）。
   *
   * @param {Function} openModal 页面 useModal 句柄的打开回调。
   * @returns {Promise<boolean>} 是否最终展示了召回弹窗。
   */
  @autobind
  openRecallGate(openModal = noop) {
    if (this.recallGatePromise) {
      return this.recallGatePromise;
    }

    this.toast.loading();
    this.recallGatePromise = this.loadFriends()
      .then(friends => {
        // 加载失败时 loadFriends 已 Toast 且 loadSucceeded 为 false：antd 单例 Toast
        // 的 fail 已顶掉 loading 并自动消退，此处不再 hide 以免截断失败文案
        if (!this.loadSucceeded) {
          return false;
        }

        if (friends.length === 0) {
          this.onCreatePoster();
          this.toast.hide();
          return false;
        }

        // 预取结果直接作为弹窗首屏：曝光埋点在此补报，挂载时跳过重复请求
        this.gatePrefetched = true;
        $$tracker.pageRecallFriendList(String(friends.length));
        openModal();
        this.toast.hide();
        return true;
      })
      .then(displayed => {
        this.recallGatePromise = null;
        return displayed;
      });

    return this.recallGatePromise;
  }

  /**
   * 挂载时消费门控预取：预取数据归属本次挂载，登记 load/reported token
   * 使卸载失效与重复上报语义与常规挂载一致。
   *
   * @param {number} mountToken 本次挂载标识。
   * @returns {boolean} 是否复用了预取数据（true 时跳过挂载重拉）。
   */
  @autobind
  consumeGatePrefetch(mountToken) {
    if (!this.gatePrefetched) {
      return false;
    }

    this.gatePrefetched = false;
    this.loadMountToken = mountToken;
    this.reportedMountToken = mountToken;
    return true;
  }

  @autobind
  invalidateMount(mountToken) {
    if (mountToken === this.activeMountToken) {
      this.activeMountToken = null;
    }
    if (mountToken === this.loadMountToken) {
      this.loadGeneration += 1;
      this.loadMountToken = null;
    }
  }

  @autobind
  loadFriends(mountToken = this.activeMountToken) {
    const generation = this.loadGeneration + 1;
    this.loadGeneration = generation;
    this.loadMountToken = mountToken;
    this.loading = true;
    this.loadSucceeded = false;
    this.mode = 'list';
    this.pendingFriend = null;

    return Promise.resolve()
      .then(() => this.services.loadRecallFriends())
      .then(
        friends => {
          if (generation !== this.loadGeneration) {
            return undefined;
          }
          this.friends = friends;
          this.loadSucceeded = true;
          if (this.reportedMountToken !== mountToken) {
            $$tracker.pageRecallFriendList(String(friends.length));
            this.reportedMountToken = mountToken;
          }
          return friends;
        },
        error => {
          if (generation !== this.loadGeneration) {
            return undefined;
          }
          this.toast.fail(error.message);
          return undefined;
        }
      )
      .then(result => {
        if (generation === this.loadGeneration) {
          this.loading = false;
        }
        return result;
      });
  }

  @autobind
  selectFriend(friend) {
    if (this.mode !== 'list' || this.submitting || !friend || friend.invited) {
      return false;
    }

    this.pendingFriend = friend;
    this.mode = 'confirm';
    $$tracker.clickRecallFriend();
    return true;
  }

  @autobind
  cancelConfirm() {
    if (this.mode !== 'confirm' || this.submitting) {
      return false;
    }

    this.pendingFriend = null;
    this.mode = 'list';
    return true;
  }

  @autobind
  confirmRecall() {
    if (this.submitPromise) {
      return this.submitPromise;
    }
    if (this.mode !== 'confirm' || !this.pendingFriend || this.pendingFriend.invited) {
      return undefined;
    }

    const target = this.pendingFriend;
    this.submitting = true;
    let submitResult;
    try {
      submitResult = this.services.recallFriend(target.relationType);
    } catch (error) {
      submitResult = Promise.reject(error);
    }
    this.submitPromise = Promise.resolve(submitResult)
      .then(
        () => {
          target.invited = true;
          this.mode = 'success';
        },
        error => {
          this.toast.fail(error.message);
          this.pendingFriend = null;
          this.mode = 'list';
        }
      )
      .then(() => {
        this.submitting = false;
        this.submitPromise = null;
      });

    return this.submitPromise;
  }

  @autobind
  returnToList() {
    if (this.mode !== 'success' || this.submitting) {
      return false;
    }

    this.pendingFriend = null;
    this.mode = 'list';
    return true;
  }

  @autobind
  createPosterInvite() {
    if (this.mode !== 'list' || this.submitting) {
      return false;
    }

    $$tracker.clickInviteEntry('生成队伍邀约海报');
    this.onCreatePoster();
    return true;
  }

  @autobind
  requestClose(closeModal = this.mountOnClose) {
    if (this.submitting) {
      return false;
    }

    this.isOpen = false;
    this.isActive = false;
    this.onOwnerClose();
    closeModal();
    this.clearStyle();
    return true;
  }
}

export default FriendRecallModalStore;
